// components/TestRunner/rules/Rule06_SOR_NamingStandard_Shared.js
import { RuleUtils } from "../../../utils/RuleUtils.js";

/**
 * Rule06_SOR_NamingStandard_Shared.js
 *
 * 🔧 Classe partagée pour les validations de standard de nommage SOR
 *
 * 🎯 Responsabilités :
 * • Contrôle RuleUtils.parseSorFileName() pour extraction des éléments
 * • Validation extension .sor et séparateur _OE_
 * • Validation préfixe contre fiber.ropRef.PBO_VALUE avec conversion de format
 * • Validation position B2-T[X]-M[Y]-[ZZZ]
 * • Validation longueur d'onde 1310nm ou 1550nm
 * • Validation numéro de tentative optionnel -[chiffre] ou _[chiffre]
 */

export class Rule06_SOR_NamingStandard_Shared {
  /**
   * Valider les fichiers SOR pour conformité au standard de nommage
   * @param {Array} files - Tableau des fichiers à valider
   * @param {Object} fiberContext - Contexte de la fibre (fiber object)
   * @param {string} ruleCode - Code de la règle (R06a, R06b, R06c)
   * @returns {Array} Messages d'erreur générés
   */
  static validateFiles(files, fiberContext, ruleCode) {
    // Analyser tous les fichiers .sor
    const analysisResults = this._analyzeFiles(files, fiberContext);

    // Regrouper les erreurs par type
    const errorsByType = this._groupErrorsByType(analysisResults);

    // Générer les messages d'erreur
    const errorMessages = [];
    Object.entries(errorsByType).forEach(([errorType, fileResults]) => {
      if (fileResults.length > 0) {
        const errorInfo = this._getErrorInfo(errorType, fileResults, fiberContext);
        errorMessages.push({
          type: "error",
          rule: ruleCode,
          title: errorInfo.title,
          message: errorInfo.message,
        });
      }
    });

    return errorMessages;
  }

  /**
   * Analyser tous les fichiers .sor
   * @param {Array} files - Liste des fichiers
   * @param {Object} fiber - Objet fibre pour validation
   * @returns {Array} Résultats d'analyse pour chaque fichier
   */
  static _analyzeFiles(files, fiber) {
    return files.map((file) => this._validateFileNaming(file.name || file.fileName || "", fiber));
  }

  /**
   * Valider le nommage d'un fichier
   * @param {string} fileName - Nom du fichier
   * @param {Object} fiber - Objet fibre pour validation
   * @returns {Object} Résultat de validation
   */
  static _validateFileNaming(fileName, fiber) {
    const elements = RuleUtils.parseSorFileName(fileName);

    const errors = [];

    // Validation format global
    if (!elements.hasValidExtension || !fileName.includes("_OE_")) {
      errors.push("format_global");
    }

    // Validation préfixe contre BPO (logique améliorée)
    if (elements.prefix) {
      const isPrefixValid = this._validatePboPrefix(elements.prefix, fiber);
      if (!isPrefixValid) {
        errors.push("prefixe_incorrect");
      }
    }

    // Validation position
    if (!elements.hasValidPosition) {
      errors.push("position_invalide");
    }

    // Validation longueur d'onde
    if (!elements.hasValidWavelength) {
      errors.push("longueur_onde_invalide");
    }

    // Validation suffixe (numéro de tentative)
    if (!elements.hasValidSuffix) {
      errors.push("suffixe_invalide");
    }

    return {
      fileName: fileName,
      errors: errors,
      elements: elements,
    };
  }

  /**
   * Valider le préfixe PBO par normalisation et comparaison directe
   * @param {string} sorPrefix - Préfixe extrait du nom de fichier SOR
   * @param {Object} fiber - Objet fibre
   * @returns {boolean} True si le préfixe est valide
   */
  static _validatePboPrefix(sorPrefix, fiber) {
    if (!fiber.ropRef?.PBO_VALUE) {
      return false;
    }

    // Normaliser les deux préfixes pour comparaison
    const normalizedSorPrefix = this.parseAndNormalizePbo(sorPrefix);
    const normalizedRopPbo = this.parseAndNormalizePbo(fiber.ropRef.PBO_VALUE);

    return normalizedSorPrefix === normalizedRopPbo;
  }

  /**
   * Parser et normaliser un préfixe PBO pour comparaison
   *
   * Logique métier : Supprime les préfixes standards (SRO-BPI-, PBO-, BE-, etc.),
   * extrait la partie avant _B2- si présente, et normalise les séparateurs.
   * Permet de comparer des PBO provenant de différentes sources (fichiers SOR, base ROP)
   * qui utilisent des formats différents mais représentent le même PBO.
   *
   * @param {string} input - Chaîne à parser (nom de fichier, PBO_VALUE, PBO_RAW, etc.)
   * @returns {string} Préfixe PBO normalisé (séparateurs en -)
   *
   * @example
   * parseAndNormalizePbo('SRO-BPI-11557315_PBO-SC-121111165-070_B2-T1-MA-002_OE_1310')
   * // Retourne: '11557315-SC-121111165-070'
   *
   * parseAndNormalizePbo('PBO-SRO-BPI-11557315-SC-121111165-070')
   * // Retourne: '11557315-SC-121111165-070'
   */
  static parseAndNormalizePbo(input) {
    let result = input;

    result = result.replace(/_PBO/g, "-");
    result = result.replace(/_BE/g, "-");

    // Suppression des préfixes standards dans l'ordre (long avant court pour éviter suppressions partielles)
    const patternsToRemove = [
      "PBO-SRO-BPI-", // Format base ROP avec PBO
      "BE-SRO-BPI-", // Format base ROP avec BE
      "SRO-BPI-", // Format fichier SOR standard
      "SRO-PBI-", // Variante typo
      "PBO-", // Préfixe court PBO
      "BE-", // Préfixe court BE
    ];

    patternsToRemove.forEach((pattern) => {
      result = result.replace(pattern, "");
    });

    // Extraction de la partie avant _B2- (position physique) si présente
    if (result.includes("_B2-")) {
      result = result.split("_B2-")[0];
    }

    // Normalisation des séparateurs : tous les _ deviennent des -
    result = result.replace(/_/g, "-");

    // Nettoyer les doubles tirets consécutifs pour les cas ou on supprime _PBOxxx
    result = result.replace(/-+/g, "-");

    return result;
  }

  /**
   * Regrouper les erreurs par type
   * @param {Array} analysisResults - Résultats d'analyse
   * @returns {Object} Erreurs regroupées par type
   */
  static _groupErrorsByType(analysisResults) {
    const errorsByType = {
      format_global: [],
      prefixe_incorrect: [],
      position_invalide: [],
      longueur_onde_invalide: [],
      suffixe_invalide: [],
    };

    analysisResults.forEach((result) => {
      result.errors.forEach((errorType) => {
        errorsByType[errorType].push(result);
      });
    });

    return errorsByType;
  }

  /**
   * Obtenir les informations d'erreur pour un type donné
   * @param {string} errorType - Type d'erreur
   * @param {Array} files - Fichiers avec cette erreur
   * @param {Object} fiber - Objet fibre
   * @returns {Object} Informations d'erreur
   */
  static _getErrorInfo(errorType, files, fiber) {
    switch (errorType) {
      case "format_global":
        return {
          title: "Fichiers SOR avec format de base invalide",
          message: this._generateFormatGlobalMessage(files),
        };
      case "prefixe_incorrect":
        return {
          title: "Fichiers SOR avec préfixe PBO incorrect",
          message: this._generatePrefixMessage(files, fiber),
        };
      case "position_invalide":
        return {
          title: "Fichiers SOR avec position physique invalide (Tiroirs / Module / Numéro de fibre)",
          message: this._generatePositionMessage(files),
        };
      case "longueur_onde_invalide":
        return {
          title: "Fichiers SOR avec longueur d'onde invalide",
          message: this._generateWavelengthMessage(files),
        };
      case "suffixe_invalide":
        return {
          title: "Fichiers SOR avec numéro de tentative invalide",
          message: this._generateSuffixMessage(files),
        };
      default:
        return {
          title: "Fichiers SOR non conformes",
          message: "Erreur inconnue détectée",
        };
    }
  }

  /**
   * Générer le message pour erreurs de format global
   */
  static _generateFormatGlobalMessage(files) {
    let message = `<strong>⚠️ Problème détecté :</strong><br>`;
    message += `Fichiers SOR avec format de base invalide<br><br>`;

    message += `<strong>Détail du problème :</strong><br>`;
    files.forEach((file) => {
      const hasValidExtension = file.elements.hasValidExtension;
      const hasOESeparator = file.fileName.includes("_OE_");
      message += `<strong>${file.fileName}</strong><br>`;
      message += `- Extension .sor présente : ${hasValidExtension ? "✅" : "❌"}<br>`;
      message += `- Séparateur _OE_ présent : ${hasOESeparator ? "✅" : "❌"}<br><br>`;
    });

    message += `<strong>⚡ Action requise :</strong><br>`;
    message += `- Corriger le format de base du fichier selon le standard SOR`;

    return message;
  }

  /**
   * Générer le message pour erreurs de préfixe
   */
  static _generatePrefixMessage(files, fiber) {
    let message = `<strong>⚠️ Problème détecté :</strong><br>`;
    message += `Fichiers SOR avec préfixe PBO incorrect<br><br>`;

    message += `<strong>Détail du problème :</strong><br>`;
    files.forEach((file) => {
      message += `<strong>${file.fileName}</strong><br>`;
      message += `- PBO configuré dans le nom du fichier SOR : ${file.elements.prefix || "Non détecté"}<br>`;
      message += `- PBO dans la base ROP : ${fiber.ropRef?.PBO_VALUE || "Non disponible"}<br>`;
      message += `- Correspondance nom du fichier / valeur dans la base ROP : ❌<br><br>`;
    });

    message += `<strong>⚡ Action requise :</strong><br>`;
    message += `- Corriger le nom du PBO dans le fichier SOR ou dans la base ROP`;

    return message;
  }

  /**
   * Générer le message pour erreurs de position
   */
  static _generatePositionMessage(files) {
    let message = `<strong>⚠️ Problème détecté :</strong><br>`;
    message += `Fichiers SOR avec position physique invalide (Tiroirs / Module / Numéro de fibre)<br><br>`;

    message += `<strong>Détail du problème :</strong><br>`;
    files.forEach((file) => {
      message += `<strong>${file.fileName}</strong><br>`;
      message += `- Format position attendu : B2-T[X]-M[Y]-[ZZZ]<br>`;
      message += `- Position configurée dans le nom du fichier SOR : ${file.elements.position || "Non détectée"}<br>`;
      message += `- Format position valide : ❌<br><br>`;
    });

    message += `<strong>⚡ Action requise :</strong><br>`;
    message += `- Corriger la position physique dans le nom du fichier SOR selon le format standard`;

    return message;
  }

  /**
   * Générer le message pour erreurs de longueur d'onde
   */
  static _generateWavelengthMessage(files) {
    let message = `<strong>⚠️ Problème détecté :</strong><br>`;
    message += `Fichiers SOR avec longueur d'onde invalide<br><br>`;

    message += `<strong>Détail du problème :</strong><br>`;
    files.forEach((file) => {
      message += `<strong>${file.fileName}</strong><br>`;
      message += `- Longueurs d'onde autorisées : 1310 nm, 1550 nm<br>`;
      message += `- Longueur d'onde configurée dans le nom du fichier SOR : ${file.elements.wavelength || "Non détectée"}<br>`;
      message += `- Longueur d'onde valide : ❌<br><br>`;
    });

    message += `<strong>⚡ Action requise :</strong><br>`;
    message += `- Corriger la longueur d'onde dans le nom du fichier SOR selon les valeurs autorisées`;

    return message;
  }

  /**
   * Générer le message pour erreurs de suffixe
   */
  static _generateSuffixMessage(files) {
    let message = `<strong>⚠️ Problème détecté :</strong><br>`;
    message += `Fichiers SOR avec numéro de tentative invalide<br><br>`;

    message += `<strong>Détail du problème :</strong><br>`;
    files.forEach((file) => {
      message += `<strong>${file.fileName}</strong><br>`;
      message += `- Formats numéro de tentative autorisés : -[chiffre], _[chiffre]<br>`;
      message += `- Numéro de tentative configuré dans le nom du fichier SOR : ${file.elements.suffix || "Non détecté"}<br>`;
      message += `- Numéro de tentative valide : ❌<br><br>`;
    });

    message += `<strong>⚡ Action requise :</strong><br>`;
    message += `- Corriger le numéro de tentative dans le nom du fichier SOR selon les formats autorisés`;

    return message;
  }
}
